import React from 'react';
import {
  CheckIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { useCartContext } from '../../context/cartContext';
import { Link } from 'react-router-dom';
import CardVorschau from './CardVorschau';

const ShoppingCart = () => {
  const {
    cart,
    clearCart,
    total_amount,
    shippingFee,
    removeItem,
    toggleAmount,
  } = useCartContext();

  if (cart.length < 1) {
    return (
      <main className="py-24">
        <div className="px-4 py-16 text-center sm:px-6 lg:px-8">
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
            Warenkorb
          </h1>
        </div>
        <div className="text-center px-6">
          <p>
            Du hast aktuell keine Produkte im Warenkorb. Sieh dir alle unsere
            Produkte an!
          </p>
          <Link
            to="/produkte"
            className="block bg-svisit-primary-600 mt-3 max-w-fit mx-auto rounded-lg px-3 py-2 text-base font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
          >
            Zu den Produkten
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="py-24">
      <div className="px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
          Warenkorb
        </h1>
      </div>

      <div className="bg-white">
        <div className="mx-auto max-w-2xl px-4 pb-24 sm:px-6 lg:max-w-7xl lg:px-8">
          <form className="mt-12 lg:grid lg:grid-cols-12 lg:items-start lg:gap-x-12 xl:gap-x-16">
            <section aria-labelledby="cart-heading" className="lg:col-span-7">
              <h2 id="cart-heading" className="sr-only">
                Produkte in deinem Warenkorb
              </h2>

              <ul
                role="list"
                className="divide-y divide-gray-200 border-b border-t border-gray-200"
              >
                {cart.map((product, productIdx) => (
                  <li key={product.id} className="flex py-6 sm:py-10">
                    <div className="shrink-0">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="h-24 w-24 rounded-md object-cover object-center sm:h-48 sm:w-48"
                      />
                    </div>

                    <div className="ml-4 flex flex-1 flex-col justify-between sm:ml-6">
                      <div className="relative pr-9 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:pr-0">
                        <div>
                          <div className="flex justify-between">
                            <h3 className="text-sm">
                              <a
                                href={`produkte/${product.hrefId}`}
                                className="font-medium text-gray-700 hover:text-gray-800"
                              >
                                {product.name}
                              </a>
                            </h3>
                          </div>
                          <p className="mt-1 text-sm font-medium text-gray-900">
                            CHF {product.price / 100}
                          </p>
                        </div>

                        <div className="mt-4 sm:mt-0 sm:pr-9">
                          <label
                            htmlFor={`quantity-${productIdx}`}
                            className="sr-only"
                          >
                            Menge, {product.name}
                          </label>
                          <select
                            id={`quantity-${productIdx}`}
                            name={`quantity-${productIdx}`}
                            defaultValue={product.amount}
                            onChange={(e) =>
                              toggleAmount(product.id, Number(e.target.value))
                            }
                            className="max-w-full rounded-md border border-gray-300 px-3 py-1.5 text-left text-base font-medium leading-5 text-gray-700 shadow-xs focus:border-svisit-primary-500 focus:outline-hidden focus:ring-1 focus:ring-svisit-primary-500 sm:text-sm"
                          >
                            {[...Array(product.max)]
                              .map((number, index) => index + 1)
                              .map((i) => (
                                <option key={i} value={i}>
                                  {i}
                                </option>
                              ))}
                          </select>

                          <div className="absolute right-0 top-0">
                            <button
                              type="button"
                              onClick={() => removeItem(product.id)}
                              className="-m-2 inline-flex p-2 text-gray-400 hover:text-gray-500"
                            >
                              <span className="sr-only">Entfernen</span>
                              <XMarkIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Bilder von Custom-Karten */}
                      {product.hasCustomization && (
                        <div className="relative pr-9 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:pr-0 mt-4">
                          <CardVorschau bildInfos={product.graphicFront} />
                          <CardVorschau bildInfos={product.graphicBack} />
                        </div>
                      )}

                      <p className="mt-4 flex space-x-2 text-sm text-gray-700">
                        {product.max > product.amount ? (
                          <CheckIcon
                            className="h-5 w-5 shrink-0 text-green-500"
                            aria-hidden="true"
                          />
                        ) : (
                          <ClockIcon
                            className="h-5 w-5 shrink-0 text-gray-300"
                            aria-hidden="true"
                          />
                        )}

                        <span>
                          {product.max > product.amount
                            ? 'Auf Lager'
                            : `Wird geliefert in ${product.leadTime}`}
                        </span>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* Order summary */}
            <section
              aria-labelledby="summary-heading"
              className="mt-16 rounded-lg bg-gray-50 px-4 py-6 sm:p-6 lg:col-span-5 lg:mt-0 lg:p-8"
            >
              <h2
                id="summary-heading"
                className="text-lg font-medium text-gray-900"
              >
                Zusammenfassung deiner Bestellung
              </h2>

              <dl className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-gray-600">Zwischentotal</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    CHF {Math.round(total_amount) / 100}
                  </dd>
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                  <dt className="flex items-center text-sm text-gray-600">
                    <span>Lieferkosten</span>
                    {/* Später für Aufschlüsselung express oder normaler Versand */}
                    {/* <a
                      href="#"
                      className="ml-2 shrink-0 text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">
                        Learn more about how shipping is calculated
                      </span>
                      <QuestionMarkCircleIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </a> */}
                  </dt>
                  <dd className="text-sm font-medium text-gray-900">
                    CHF {shippingFee / 100}
                  </dd>
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                  <dt className="flex text-sm text-gray-600">
                    <span>Enthaltene Mehrwertsteuer</span>
                  </dt>
                  <dd className="text-sm font-medium text-gray-900">
                    CHF {Math.round((total_amount + shippingFee) * 0.081) / 100}
                  </dd>
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                  <dt className="text-base font-medium text-gray-900">
                    Gesamtbetrag
                  </dt>
                  <dd className="text-base font-medium text-gray-900">
                    CHF {(total_amount + shippingFee) / 100}
                  </dd>
                </div>
              </dl>

              <div className="mt-6">
                <Link
                  to="/warenkorb/bestellung"
                  className="block text-center w-full rounded-md border border-transparent bg-svisit-primary-600 px-4 py-3 text-base font-medium text-white shadow-xs hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                >
                  Zur Bestellung
                </Link>
              </div>
            </section>
          </form>
        </div>
      </div>
    </main>
  );
};

export default ShoppingCart;
